// M29 — Reset password page. Lands here from the recovery link in the
// email Supabase sent. Supabase parses the URL fragment / query params
// and emits a PASSWORD_RECOVERY auth-state event that gives us a
// recovery session — at that point `auth.updateUser({ password })`
// works without the user having to re-enter the old password.
//
// We accept input either way: if PASSWORD_RECOVERY hasn't fired yet (or
// the link is bad), `updatePassword` will return a clear error that we
// surface via `friendlyAuthError`. Better than gating the form behind a
// state machine and leaving the user stuck on a spinner if Supabase
// doesn't emit the event for some reason.

import { useEffect, useState, type FormEvent } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import QSLogo from '../../components/QSLogo';
import AuthPageFooter from '../../components/AuthPageFooter';
import { useAuth } from '../../contexts/useAuth';
import { friendlyAuthError } from '../../lib/authErrors';
import { supabase } from '../../lib/supabase';

export default function ResetPasswordPage() {
  const { updatePassword } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recoveryReady, setRecoveryReady] = useState(false);

  // Watch for the PASSWORD_RECOVERY event Supabase fires after parsing
  // the recovery token from the URL. Once it fires we know the user has
  // an active recovery session and the form is safe to submit.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setRecoveryReady(true);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);
    const { error: err } = await updatePassword(password);
    setLoading(false);

    if (err) {
      setError(friendlyAuthError(err.message));
      return;
    }
    // Success — user is now signed in with the new password. Send them
    // to the dashboard. The auth-state listener already updated `user`.
    navigate('/', { replace: true });
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      bgcolor: 'background.default',
      px: 2,
      py: 4,
      gap: 3,
    }}>
      <Card sx={{ maxWidth: 420, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <QSLogo variant="icon" height={52} />
            <Box sx={{ mt: 1.5, mb: 0.5 }}>
              <QSLogo variant="wordmark" />
            </Box>
            <Typography variant="body2" color="text.secondary">Choose a new password</Typography>
          </Box>

          {!recoveryReady && (
            <Alert severity="info" sx={{ mb: 2 }}>
              If you arrived here from a password-reset email, we're verifying your link.
              You can still set a new password — we'll let you know if anything's off.
            </Alert>
          )}

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              label="New password"
              type="password"
              fullWidth
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              sx={{ mb: 2 }}
              helperText="At least 6 characters."
            />
            <TextField
              label="Confirm new password"
              type="password"
              fullWidth
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{ mb: 3 }}
            />
            <Button type="submit" variant="contained" fullWidth size="large" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Update password'}
            </Button>
          </Box>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Need a fresh link?{' '}
              <Link component={RouterLink} to="/forgot-password">Request another</Link>
            </Typography>
          </Box>
        </CardContent>
      </Card>
      <AuthPageFooter />
    </Box>
  );
}
